import React, { useEffect, useState } from 'react';
import AppHeader from '../../../components/AppHeader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBusinessTime,
  faCalendar,
  faCheckCircle,
  faClock,
  faEdit,
  faExclamationTriangle,
  faSave,
  faSpinner,
  faStore,
  faTimes,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { useEmployeeStore } from '../../../features/services/store/useEmployeeStore.ts';
import { useWorkingHoursStore } from '../../../features/services/store/useWorkingHoursStore.ts';
import { useCurrentSalonStore } from '@stores/useCurrentSalonStore.ts';

const WorkingHoursPage: React.FC = () => {
  const [selectedStaff, setSelectedStaff] = useState<string>('');
  const [editingHours, setEditingHours] = useState<boolean>(false);
  const [tempWorkingHours, setTempWorkingHours] = useState<{ [key: number]: { startTime: string; endTime: string; isActive: boolean } }>({});
  const salonId = useCurrentSalonStore(s => s.currentSalon?.salonId);

  // Stores
  const { employees, loading: employeesLoading, loadEmployees } = useEmployeeStore();
  const { loading: workingHoursLoading, fetchWorkingHoursByUser, createWorkingHours, updateWorkingHours } = useWorkingHoursStore();
  const [staffWorkingHoursList, setStaffWorkingHoursList] = useState<Array<{ id: string; weekday: number; startTime: string; endTime: string; isActive: boolean }>>([]);

  const breadcrumb = [{ label: 'Zaměstnanci' }, { label: 'Pracovní doby' }];

  // Load employees for salon from URL
  useEffect(() => {
    if (salonId) {
      loadEmployees({ salonId });
    }
  }, [salonId, loadEmployees]);

  // Load working hours for the selected employee (user)
  const loadStaffWorkingHours = async (userId: string) => {
    try {
      const staffWorkingHours = await fetchWorkingHoursByUser(userId);
      setStaffWorkingHoursList(staffWorkingHours);
      const tempHours: { [key: number]: { startTime: string; endTime: string; isActive: boolean } } = {};
      staffWorkingHours.forEach(wh => {
        tempHours[wh.weekday] = {
          startTime: wh.startTime,
          endTime: wh.endTime,
          isActive: wh.isActive,
        };
      });
      setTempWorkingHours(tempHours);
    } catch (error) {
      console.error('Error loading working hours:', error);
    }
  };

  useEffect(() => {
    if (selectedStaff) {
      loadStaffWorkingHours(selectedStaff);
    } else {
      setTempWorkingHours({});
      setStaffWorkingHoursList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStaff]);

  const days = [
    { id: 1, name: 'Pondělí', short: 'Po' },
    { id: 2, name: 'Úterý', short: 'Út' },
    { id: 3, name: 'Středa', short: 'St' },
    { id: 4, name: 'Čtvrtek', short: 'Čt' },
    { id: 5, name: 'Pátek', short: 'Pá' },
    { id: 6, name: 'Sobota', short: 'So' },
    { id: 7, name: 'Neděle', short: 'Ne' },
  ];

  const selectedEmployee = employees.find(emp => emp.id === selectedStaff);
  const selectedEmployeeName = selectedEmployee ? `${selectedEmployee.firstName ?? ''} ${selectedEmployee.lastName ?? ''}`.trim() : '';

  const handleTimeChange = (weekday: number, field: 'startTime' | 'endTime', value: string) => {
    setTempWorkingHours(prev => ({
      ...prev,
      [weekday]: {
        ...prev[weekday],
        [field]: value,
        isActive: prev[weekday]?.isActive ?? true,
      },
    }));
  };

  const handleWorkingToggle = (weekday: number, isActive: boolean) => {
    setTempWorkingHours(prev => ({
      ...prev,
      [weekday]: {
        ...prev[weekday],
        startTime: prev[weekday]?.startTime ?? '09:00',
        endTime: prev[weekday]?.endTime ?? '17:00',
        isActive,
      },
    }));
  };

  const saveWorkingHours = async () => {
    if (!selectedStaff || !salonId) return;

    try {
      // Save or update working hours for each day
      for (const [weekday, hours] of Object.entries(tempWorkingHours)) {
        const weekdayNum = parseInt(weekday);
        const existingHours = staffWorkingHoursList.find(wh => wh.weekday === weekdayNum);

        if (existingHours) {
          // Update existing
          await updateWorkingHours(existingHours.id, {
            startTime: hours.startTime,
            endTime: hours.endTime,
            isActive: hours.isActive,
          });
        } else {
          // Create new
          await createWorkingHours({
            userId: selectedStaff,
            weekday: weekdayNum,
            startTime: hours.startTime,
            endTime: hours.endTime,
            isActive: hours.isActive,
          });
        }
      }

      setEditingHours(false);
      // Reload after save
      await loadStaffWorkingHours(selectedStaff);
    } catch (error) {
      console.error('Error saving working hours:', error);
    }
  };

  const cancelEditing = () => {
    setEditingHours(false);
    if (selectedStaff) {
      loadStaffWorkingHours(selectedStaff);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Pracovní doby" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {/* Salon Switcher */}
          <section className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-gray-900">Aktivní salon</h2>
                <p className="text-sm text-gray-600 mt-1">Vyberte salon pro správu pracovních hodin</p>
              </div>
            </div>
          </section>

          {/* Header */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Pracovní doby</h1>
                <p className="text-gray-600 mt-1">Spravujte pracovní doby zaměstnanců</p>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setEditingHours(true)}
                  disabled={!selectedStaff || !salonId}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  <FontAwesomeIcon icon={faEdit} className="mr-2" />
                  Upravit doby
                </button>
                {editingHours && (
                  <button onClick={saveWorkingHours} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                    Uložit
                  </button>
                )}
                {editingHours && (
                  <button onClick={cancelEditing} className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors">
                    <FontAwesomeIcon icon={faTimes} className="mr-2" />
                    Zrušit
                  </button>
                )}
              </div>
            </div>
          </section>

          {/* Staff Selection */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Výběr zaměstnance</h2>
            {!salonId ? (
              <div className="text-center py-8">
                <FontAwesomeIcon icon={faStore} className="w-12 h-12 text-gray-400 mb-4" />
                <p className="text-gray-500">Neplatný salon ID</p>
              </div>
            ) : employeesLoading ? (
              <div className="text-center py-8">
                <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-gray-400 mb-4 animate-spin" />
                <p className="text-gray-500">Načítání zaměstnanců...</p>
              </div>
            ) : employees.length === 0 ? (
              <div className="text-center py-8">
                <FontAwesomeIcon icon={faUser} className="w-12 h-12 text-gray-400 mb-4" />
                <p className="text-gray-500">Žádní zaměstnanci nenalezeni pro vybraný salon</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {employees.map(employee => {
                  const fullName = `${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim();
                  return (
                    <button
                      key={employee.id}
                      onClick={() => setSelectedStaff(employee.id)}
                      className={`p-4 rounded-lg border-2 transition-colors ${
                        selectedStaff === employee.id ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <FontAwesomeIcon icon={faUser} className="w-5 h-5 text-green-600" />
                        </div>
                        <div className="text-left">
                          <div className="font-medium text-gray-900">{fullName || employee.email}</div>
                          <div className="text-sm text-gray-600">{employee.role}</div>
                          <div className="text-xs text-gray-500">{employee.status}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          {/* Working Hours Table */}
          {selectedEmployee && (
            <section className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Pracovní doby - {selectedEmployeeName}</h3>
              </div>
              <div className="p-6">
                {workingHoursLoading ? (
                  <div className="text-center py-8">
                    <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-gray-400 mb-4 animate-spin" />
                    <p className="text-gray-500">Načítání pracovních hodin...</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Den</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pracuje</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Začátek</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Konec</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Celkem hodin</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {days.map(day => {
                            const dayHours = tempWorkingHours[day.id];
                            const isWorking = dayHours?.isActive || false;
                            const startTime = dayHours?.startTime || '09:00';
                            const endTime = dayHours?.endTime || '17:00';

                            // Calculate total hours
                            const start = new Date(`2000-01-01T${startTime}:00`);
                            const end = new Date(`2000-01-01T${endTime}:00`);
                            const diffMs = end.getTime() - start.getTime();
                            const diffHours = Math.round((diffMs / (1000 * 60 * 60)) * 10) / 10;

                            return (
                              <tr key={day.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <FontAwesomeIcon icon={faCalendar} className="w-4 h-4 mr-2 text-gray-400" />
                                    <span className="text-sm font-medium text-gray-900">{day.name}</span>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {editingHours ? (
                                    <label className="flex items-center">
                                      <input
                                        type="checkbox"
                                        checked={isWorking}
                                        onChange={e => handleWorkingToggle(day.id, e.target.checked)}
                                        className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                                      />
                                      <span className="ml-2 text-sm text-gray-900">{isWorking ? 'Ano' : 'Ne'}</span>
                                    </label>
                                  ) : (
                                    <span
                                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                        isWorking ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                      }`}
                                    >
                                      <FontAwesomeIcon icon={isWorking ? faCheckCircle : faExclamationTriangle} className="w-3 h-3 mr-1" />
                                      {isWorking ? 'Ano' : 'Ne'}
                                    </span>
                                  )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {editingHours ? (
                                    <input
                                      type="time"
                                      value={startTime}
                                      onChange={e => handleTimeChange(day.id, 'startTime', e.target.value)}
                                      disabled={!isWorking}
                                      className="px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 disabled:bg-gray-100"
                                    />
                                  ) : (
                                    <div className="flex items-center">
                                      <FontAwesomeIcon icon={faClock} className="w-4 h-4 mr-2 text-gray-400" />
                                      <span className="text-sm text-gray-900">{isWorking ? startTime : '-'}</span>
                                    </div>
                                  )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {editingHours ? (
                                    <input
                                      type="time"
                                      value={endTime}
                                      onChange={e => handleTimeChange(day.id, 'endTime', e.target.value)}
                                      disabled={!isWorking}
                                      className="px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 disabled:bg-gray-100"
                                    />
                                  ) : (
                                    <div className="flex items-center">
                                      <FontAwesomeIcon icon={faClock} className="w-4 h-4 mr-2 text-gray-400" />
                                      <span className="text-sm text-gray-900">{isWorking ? endTime : '-'}</span>
                                    </div>
                                  )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="text-sm font-medium text-gray-900">{isWorking ? `${diffHours}h` : '0h'}</span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Summary */}
                    <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <FontAwesomeIcon icon={faBusinessTime} className="w-5 h-5 mr-2 text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">Celkem pracovních hodin týdně:</span>
                        </div>
                        <span className="text-lg font-bold text-gray-900">
                          {days
                            .reduce((total, day) => {
                              const dayHours = tempWorkingHours[day.id];
                              if (dayHours?.isActive) {
                                const start = new Date(`2000-01-01T${dayHours.startTime}:00`);
                                const end = new Date(`2000-01-01T${dayHours.endTime}:00`);
                                const diffMs = end.getTime() - start.getTime();
                                const diffHours = diffMs / (1000 * 60 * 60);
                                return total + diffHours;
                              }
                              return total;
                            }, 0)
                            .toFixed(1)}
                          h
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkingHoursPage;
